import {
  Offer,
  PopUpProductSequence,
  PopUpSequenceProduct
} from 'common/contracts';
import {
  EPopupSubType,
  ESaleDiscountDisplayType,
  OfferType
} from 'constants/enums';
import { v4 as uuidv4 } from 'uuid';
import { ProductEntry } from 'views/Offers/Popups/Form/hooks/usePopupProducts';

import { RewardCalendarPreviewSequenceItem } from './RewardCalendarPreview';
import {
  buildEmptyWeek,
  REWARD_CALENDAR_DAYS_PER_WEEK,
  REWARD_CALENDAR_DEFAULT_VALUES,
  RewardCalendarDay,
  RewardCalendarFormFields,
  RewardCalendarWeek
} from './RewardCalendarForm.consts';

type RewardCalendarPayload = Partial<Offer> & {
  rewardCalendar: {
    resetBehavior: RewardCalendarFormFields['missedRewardReset'];
    popupTrigger: RewardCalendarFormFields['popUpTrigger'];
    rewardCollectionBehavior: RewardCalendarFormFields['rewardCollection'];
  };
};

interface IBuildRewardCalendarPayloadParams {
  data: RewardCalendarFormFields;
}

const mapSequenceProductToEntry = (
  product: PopUpSequenceProduct
): ProductEntry =>
  ({
    id: uuidv4(),
    productId: product.product?.productId ?? '',
    quantity: product.quantity ?? 0,
    displayName: product.product?.displayName ?? '',
    image: product.product?.images?.[0]?.url ?? '',
    quantityDisplay: product?.quantityDisplay
  }) as ProductEntry;

export const mapOfferToRewardCalendarWeeks = (
  offer: Partial<Offer>
): RewardCalendarWeek[] => {
  const sequences = (offer.productsSequence as PopUpProductSequence[]) ?? [];

  if (sequences.length === 0) {
    return [buildEmptyWeek()];
  }

  const weeksCount = Math.max(
    1,
    Math.ceil(sequences.length / REWARD_CALENDAR_DAYS_PER_WEEK)
  );

  return Array.from({ length: weeksCount }, (_, weekIndex): RewardCalendarWeek => {
    const days = Array.from(
      { length: REWARD_CALENDAR_DAYS_PER_WEEK },
      (__, dayIndex): RewardCalendarDay => {
        const sequenceIndex =
          weekIndex * REWARD_CALENDAR_DAYS_PER_WEEK + dayIndex;
        const sequence = sequences[sequenceIndex];
        const products =
          sequence?.products?.map(mapSequenceProductToEntry) ?? [];

        return {
          id: uuidv4(),
          products
        };
      }
    );

    return { id: uuidv4(), days };
  });
};

interface IBuildRewardCalendarInitialValuesParams {
  currentOffer: Partial<Offer>;
  defaultOfferUiId: string;
  edit: boolean;
  dup: boolean;
  weeks: RewardCalendarWeek[];
}

export const buildRewardCalendarInitialValues = ({
  currentOffer,
  defaultOfferUiId,
  edit,
  dup,
  weeks
}: IBuildRewardCalendarInitialValuesParams): RewardCalendarFormFields => {
  const rc = currentOffer.rewardCalendar;

  const startOver =
    currentOffer.startOver != null
      ? Boolean(currentOffer.startOver)
      : REWARD_CALENDAR_DEFAULT_VALUES.startOver;

  const formDocumentId =
    edit && !dup && typeof currentOffer.offerId === 'string' && currentOffer.offerId.length > 0
      ? currentOffer.offerId
      : undefined;

  return {
    ...REWARD_CALENDAR_DEFAULT_VALUES,
    ...(formDocumentId !== undefined ? { _id: formDocumentId } : {}),
    active:
      dup || edit ? Boolean(currentOffer.active) : REWARD_CALENDAR_DEFAULT_VALUES.active,
    name: dup ? `${currentOffer.name ?? ''}_copy` : (currentOffer.name ?? ''),
    publisherOfferId: dup
      ? `${currentOffer.publisherOfferId ?? ''}_copy`
      : (currentOffer.publisherOfferId ?? ''),
    offerUiId:
      currentOffer.offerUi?._id ||
      currentOffer.offerUi?.offerUiId ||
      defaultOfferUiId,
    priority:
      currentOffer.priority ?? REWARD_CALENDAR_DEFAULT_VALUES.priority,
    subType: EPopupSubType.REWARD_CALENDAR,
    startOver,
    missedRewardReset:
      rc?.resetBehavior ??
      REWARD_CALENDAR_DEFAULT_VALUES.missedRewardReset,
    popUpTrigger:
      rc?.popupTrigger ??
      REWARD_CALENDAR_DEFAULT_VALUES.popUpTrigger,
    rewardCollection:
      rc?.rewardCollectionBehavior ??
      REWARD_CALENDAR_DEFAULT_VALUES.rewardCollection,
    segments: Array.isArray(currentOffer.segments)
      ? currentOffer.segments
      : [],
    weeks: weeks.length > 0 ? weeks : [buildEmptyWeek()]
  };
};

export const buildRewardCalendarPayload = ({
  data
}: IBuildRewardCalendarPayloadParams): RewardCalendarPayload => {
  const flatDays = data.weeks.flatMap((week) => week.days);

  const productsSequence = flatDays.map((day, index) => ({
    index: index + 1,
    priceInUsdCents: 0,
    products: day.products
      .filter(
        (product: ProductEntry): product is ProductEntry & { productId: string } =>
          typeof product.productId === 'string' && product.productId.trim() !== ''
      )
      .map((product) => ({
        productId: product.productId.trim(),
        quantity: Math.max(1, Number(product.quantity) || 1)
      }))
  }));

  return {
    active: data.active,
    coolDownInHours: 1,
    displayName: data.name,
    name: data.name,
    offerUiId: data.offerUiId,
    priceDiscount: {
      discount: 0,
      type: ESaleDiscountDisplayType.PERCENTAGE
    },
    priority: data.priority,
    productsSequence,
    publisherOfferId: data.publisherOfferId,
    segments: data.segments,
    showAfter: 'login',
    startOver: data.startOver,
    subType: EPopupSubType.REWARD_CALENDAR,
    tags: [],
    triggers: [{ type: 'time', eventName: 'impression', every: 1440 }],
    type: OfferType.POPUP,
    rewardCalendar: {
      resetBehavior: data.missedRewardReset,
      popupTrigger: data.popUpTrigger,
      rewardCollectionBehavior: data.rewardCollection
    }
  };
};

export const mapWeeksToPreviewSequence = (
  weeks: RewardCalendarFormFields['weeks']
): RewardCalendarPreviewSequenceItem[] =>
  weeks.flatMap((week, weekIndex) =>
    week.days.map((day, dayIndex) => {
      const globalIndex = weekIndex * 7 + dayIndex;
      type ProductEntryWithImage = ProductEntry & { image?: string };
      const products = (day.products as ProductEntryWithImage[])
        .filter(
          (p) => typeof p.productId === 'string' && p.productId.trim() !== ''
        )
        .map((p) => ({
          images: { product: p.image || '' },
          quantity: Number(p.quantity) || 0,
          quantityDisplay: `x${Number(p.quantity) || 0}`
        }));

      return { index: globalIndex, products };
    })
  );

export const resolveActiveWeekIndexAfterWeekRemoved = (
  activeWeekIndex: number,
  removedWeekIndex: number,
  nextWeeksLength: number
): number => {
  if (nextWeeksLength <= 0) return 0;
  if (activeWeekIndex >= nextWeeksLength) return nextWeeksLength - 1;
  if (activeWeekIndex > removedWeekIndex) return activeWeekIndex - 1;
  return activeWeekIndex;
};
